import type { ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasSchedulingAccess } from '../../profile';

/**
 * Route-level authorization guard for the Scheduling-claim-gated surgery board (spec 091).
 *
 * Mounts `children` ONLY once the profile has resolved AND the user has the **Scheduling** claim,
 * so an unauthorized user's page shell/data hooks never mount (no partial rendering, no board query
 * firing). Shows a neutral loading state while the profile is unresolved to avoid a redirect flash;
 * denies direct navigation otherwise.
 */
export function RequireSchedulingAccess({ children }: { children: ReactElement }): ReactElement | null {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="surgery-board-auth-loading" />
      </Box>
    );
  }

  if (!hasSchedulingAccess(profile))
    return <Navigate to="/" replace />;

  return children;
}
